import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { HelpChat } from "../components/HelpChat";

// Animated counter component
function AnimatedCounter({ end, duration = 2000, suffix = "" }: { end: number; duration?: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (hasAnimated) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    const element = document.getElementById(`counter-${end}`);
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return (
    <span id={`counter-${end}`} className="tabular-nums">
      {count.toLocaleString()}{suffix}
    </span>
  );
}

// Feature card component
function FeatureCard({ 
  icon, 
  title, 
  description, 
  gradient 
}: { 
  icon: React.ReactNode; 
  title: string; 
  description: string; 
  gradient: string;
}) {
  return (
    <div className="group relative p-6 sm:p-8 rounded-2xl bg-surface-900/80 border border-surface-700/50 hover:border-primary-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary-500/10 overflow-hidden">
      {/* Gradient background on hover */}
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 ${gradient}`} />
      
      <div className="relative z-10">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
        <h3 className="text-xl font-bold text-surface-100 mb-3">{title}</h3>
        <p className="text-surface-400 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

// Step card for "How it works"
function StepCard({ 
  number, 
  title, 
  description 
}: { 
  number: number; 
  title: string; 
  description: string;
}) {
  return (
    <div className="relative flex flex-col items-center text-center group">
      {/* Step number */}
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-2xl font-bold text-white mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-primary-500/30">
        {number}
      </div>
      <h3 className="text-lg font-semibold text-surface-100 mb-2">{title}</h3>
      <p className="text-surface-400 text-sm leading-relaxed max-w-xs">{description}</p>
    </div>
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="landing-page min-h-screen bg-surface-950 text-surface-100 overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-surface-950/95 backdrop-blur-xl border-b border-surface-800/50 shadow-lg" 
          : "bg-transparent"
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="TradeCraft" className="w-10 h-10" />
              <div className="hidden sm:block">
                <span className="text-xl font-bold bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                  TradeCraft
                </span>
                <p className="text-xs text-surface-500">by Colvix</p>
              </div>
            </div>

            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-surface-300 hover:text-primary-400 transition-colors">Features</a>
              <a href="#how-it-works" className="text-surface-300 hover:text-primary-400 transition-colors">How It Works</a>
              <a href="#strategies" className="text-surface-300 hover:text-primary-400 transition-colors">Strategies</a>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/login")}
                className="px-4 py-2 text-sm font-medium text-surface-300 hover:text-surface-100 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-5 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                Get Started Free
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 sm:pb-24 overflow-hidden">
        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src="/intro.mp4?v=2" type="video/mp4" />
        </video>
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-surface-950/75 z-[1]" />

        {/* Animated background elements - reduced opacity */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
          <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] bg-primary-500/5 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-1/4 -right-1/4 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: "1s" }} />
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.02)_1px,transparent_1px)] bg-[size:60px_60px] z-[3]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-800/50 border border-surface-700/50 mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-sm text-surface-300">Powered by Alpaca & Charles Schwab</span>
          </div>

          {/* Main headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 animate-fade-in" style={{ animationDelay: "100ms" }}>
            <span className="text-surface-100">Build & Backtest</span>
            <br />
            <span className="bg-gradient-to-r from-primary-400 via-accent-400 to-primary-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              Trading Strategies
            </span>
            <br />
            <span className="text-surface-100">Without Code</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-surface-400 max-w-3xl mx-auto mb-10 animate-fade-in" style={{ animationDelay: "200ms" }}>
            Transform your investment ideas into automated strategies. Backtest against historical data, 
            analyze performance metrics—all in one powerful platform.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in" style={{ animationDelay: "300ms" }}>
            <button
              onClick={() => navigate("/signup")}
              className="group px-8 py-4 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-2xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-1 flex items-center gap-3"
            >
              Start Building Free
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
            <button
              onClick={() => navigate("/search")}
              className="px-8 py-4 text-lg font-semibold rounded-xl bg-surface-800/50 border border-surface-700 text-surface-100 hover:bg-surface-700/50 hover:border-surface-600 transition-all duration-300 flex items-center gap-3"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Community Strategies
            </button>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12 lg:gap-16 animate-fade-in" style={{ animationDelay: "400ms" }}>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl font-bold text-surface-100">
                <AnimatedCounter end={100} suffix="+" />
              </p>
              <p className="text-sm text-surface-500 mt-1">Pre-built Strategies</p>
            </div>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl font-bold text-surface-100">
                <AnimatedCounter end={10000} suffix="+" />
              </p>
              <p className="text-sm text-surface-500 mt-1">Backtests Run</p>
            </div>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl font-bold text-primary-400">
                &lt;1s
              </p>
              <p className="text-sm text-surface-500 mt-1">Backtest Speed</p>
            </div>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl font-bold text-accent-400">
                100%
              </p>
              <p className="text-sm text-surface-500 mt-1">Free to Explore</p>
            </div>
          </div>
        </div>

        {/* Hero image/mockup */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-5xl px-4 animate-fade-in" style={{ animationDelay: "500ms" }}>
          <div className="relative rounded-t-2xl overflow-hidden border border-surface-700/50 border-b-0 bg-surface-900/90 backdrop-blur-xl shadow-2xl">
            <div className="flex items-center gap-2 px-4 py-3 bg-surface-800/50 border-b border-surface-700/50">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <span className="text-xs text-surface-500 ml-2">TradeCraft — Strategy Backtester</span>
            </div>
            <img 
              src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=80" 
              alt="Trading Dashboard"
              className="w-full h-48 sm:h-64 object-cover opacity-60"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-24 sm:py-32 bg-gradient-to-b from-surface-950 to-surface-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
              Powerful Features
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              Everything You Need to
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                Build Smarter Strategies
              </span>
            </h2>
            <p className="text-lg text-surface-400 max-w-2xl mx-auto">
              From idea to execution—our platform provides all the tools for systematic investment research.
            </p>
          </div>

          {/* Features grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <FeatureCard
              gradient="bg-gradient-to-br from-blue-500 to-cyan-500"
              icon={
                <svg className="w-7 h-7 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              }
              title="Lightning-Fast Backtesting"
              description="Test your strategies against years of historical data in under a second. See how your ideas would have performed across different market conditions."
            />
            
            <FeatureCard
              gradient="bg-gradient-to-br from-purple-500 to-pink-500"
              icon={
                <svg className="w-7 h-7 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
              title="Automated Rebalancing"
              description="Connect your Alpaca brokerage account for automated portfolio rebalancing. Start risk-free with paper trading before going live."
            />

            <FeatureCard
              gradient="bg-gradient-to-br from-green-500 to-emerald-500"
              icon={
                <svg className="w-7 h-7 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              title="Rich Performance Metrics"
              description="Sharpe ratio, max drawdown, alpha, beta—get the full picture with comprehensive analytics that help you understand risk and returns."
            />

            <FeatureCard
              gradient="bg-gradient-to-br from-orange-500 to-red-500"
              icon={
                <svg className="w-7 h-7 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
              title="Community Strategies"
              description="Browse and invest in strategies created by others. Learn from the community and discover new approaches to systematic investing."
            />

            <FeatureCard
              gradient="bg-gradient-to-br from-indigo-500 to-violet-500"
              icon={
                <svg className="w-7 h-7 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
              }
              title="Portfolio Visualization"
              description="Track your investments with beautiful charts showing allocation history, performance over time, and detailed position breakdowns."
            />

            <FeatureCard
              gradient="bg-gradient-to-br from-teal-500 to-cyan-500"
              icon={
                <svg className="w-7 h-7 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              }
              title="Secure & Reliable"
              description="Your credentials are encrypted and never stored. We use industry-standard security practices to keep your data safe."
            />

            <FeatureCard
              gradient="bg-gradient-to-br from-cyan-500 to-blue-500"
              icon={
                <svg className="w-7 h-7 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              }
              title="Portfolio Analyzer"
              description="Correlation matrices, Sharpe optimization, and Monte Carlo simulation with 5,000 paths — all computed client-side in under 100ms."
            />

            <FeatureCard
              gradient="bg-gradient-to-br from-emerald-500 to-teal-500"
              icon={
                <svg className="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              }
              title="Diversifier"
              description="Combine multiple strategies, set custom weights, and see projected performance across best, average, and worst case scenarios."
            />

            <FeatureCard
              gradient="bg-gradient-to-br from-amber-500 to-orange-500"
              icon={
                <svg className="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              }
              title="TradeCraft Pulse"
              description="Live market dashboard with indices, sector heatmaps, Fear & Greed, sentiment, crypto, news, and earnings — auto-refreshing every 30 seconds."
            />

            <FeatureCard
              gradient="bg-gradient-to-br from-rose-500 to-pink-500"
              icon={
                <svg className="w-7 h-7 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              }
              title="Mirror Trading"
              description="Auto-replicate every Alpaca trade to your Charles Schwab account in real time — same ticker, same quantity, same direction."
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="relative py-24 sm:py-32 bg-surface-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 text-accent-400 text-sm font-medium mb-4">
              Simple Process
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              From Idea to Execution
              <br />
              <span className="bg-gradient-to-r from-accent-400 to-primary-400 bg-clip-text text-transparent">
                In Three Steps
              </span>
            </h2>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-12 lg:gap-16 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-8 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-primary-500/20 via-accent-500/40 to-primary-500/20" />
            
            <StepCard
              number={1}
              title="Choose or Build"
              description="Browse community strategies or create your own using our intuitive strategy builder. No coding required."
            />
            <StepCard
              number={2}
              title="Backtest & Analyze"
              description="Run backtests against historical data. Analyze performance metrics and refine your approach."
            />
            <StepCard
              number={3}
              title="Automate & Research"
              description="Connect your Alpaca account and begin risk-free with paper trading. Your strategy executes automatically based on your rules."
            />
          </div>
        </div>
      </section>

      {/* Strategies Preview Section */}
      <section id="strategies" className="relative py-24 sm:py-32 bg-gradient-to-b from-surface-900 to-surface-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium mb-4">
              Strategy Library
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              Explore Diverse
              <br />
              <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                Investment Approaches
              </span>
            </h2>
            <p className="text-lg text-surface-400 max-w-2xl mx-auto">
              From momentum to value, dividend to growth—discover strategies that match your investment philosophy.
            </p>
          </div>

          {/* Strategy categories */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {[
              { name: "Momentum", icon: "📈", color: "from-blue-500 to-cyan-500" },
              { name: "Value", icon: "💎", color: "from-green-500 to-emerald-500" },
              { name: "Dividend", icon: "💰", color: "from-yellow-500 to-orange-500" },
              { name: "Growth", icon: "🚀", color: "from-purple-500 to-pink-500" },
            ].map((category) => (
              <div
                key={category.name}
                className="group relative p-6 rounded-xl bg-surface-800/50 border border-surface-700/50 hover:border-surface-600 transition-all duration-300 cursor-pointer"
                onClick={() => navigate("/search")}
              >
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity rounded-xl bg-gradient-to-br ${category.color}`} />
                <div className="text-3xl mb-3">{category.icon}</div>
                <h3 className="text-lg font-semibold text-surface-100">{category.name}</h3>
                <p className="text-sm text-surface-500">Explore strategies →</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={() => navigate("/search")}
              className="inline-flex items-center gap-2 px-6 py-3 text-lg font-semibold rounded-xl bg-surface-800 border border-surface-700 text-surface-100 hover:bg-surface-700 hover:border-surface-600 transition-all duration-300"
            >
              View All Strategies
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Alpaca Integration Section */}
      <section className="relative py-24 sm:py-32 bg-surface-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm font-medium mb-4">
                Brokerage Integration
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-surface-100 mb-6">
                Powered by
                <br />
                <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  Alpaca & Schwab
                </span>
              </h2>
              <p className="text-lg text-surface-400 mb-8 leading-relaxed">
                TradeCraft integrates with Alpaca for automated strategy execution and Charles Schwab 
                via Mirror Trading — replicate every trade to your Schwab account in real time.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Commission-free trading on US stocks & ETFs via Alpaca",
                  "Mirror Trading to Charles Schwab — same ticker, same quantity, real time",
                  "Start with paper trading—no risk to test strategies",
                  "Secure API connection—we never hold your funds",
                  "Real-time portfolio synchronization",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-surface-300">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/signup")}
                className="inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300"
              >
                Get Started
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-3xl blur-3xl" />
              <div className="relative rounded-2xl overflow-hidden border border-surface-700/50 bg-surface-900/80 shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800&q=80" 
                  alt="Trading Charts"
                  className="w-full h-64 sm:h-80 object-cover opacity-80"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-surface-950 to-accent-500/10" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.05)_1px,transparent_1px)] bg-[size:40px_40px]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-6">
            Ready to Transform Your
            <br />
            <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
              Investment Research?
            </span>
          </h2>
          <p className="text-lg text-surface-400 mb-10 max-w-2xl mx-auto">
            Join thousands of investors using TradeCraft to build, test, and execute systematic strategies. 
            Start exploring for free—no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate("/signup")}
              className="group px-8 py-4 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-2xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-1 flex items-center gap-3"
            >
              Create Free Account
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
            <button
              onClick={() => navigate("/search")}
              className="px-8 py-4 text-lg font-semibold rounded-xl bg-surface-800/50 border border-surface-700 text-surface-100 hover:bg-surface-700/50 transition-all duration-300"
            >
              Explore First
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-900 border-t border-surface-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Main footer content */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
            {/* Brand */}
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.svg" alt="TradeCraft" className="w-10 h-10" />
                <div>
                  <span className="text-lg font-bold text-surface-100">TradeCraft</span>
                  <p className="text-xs text-surface-500">by Colvix</p>
                </div>
              </div>
              <p className="text-surface-400 text-sm leading-relaxed mb-4">
                A platform for algorithmic strategy development and rule-based trading research.
              </p>
              <a
                href="https://www.colvix.net"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 hover:opacity-100 transition-opacity"
              >
                <img src="/colvix_footer_final.png" alt="Colvix" className="h-5" />
              </a>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold text-surface-100 uppercase tracking-wider mb-4">Product</h4>
              <ul className="space-y-3">
                <li><Link to="/search" className="text-surface-400 hover:text-primary-400 transition-colors text-sm">Community Strategies</Link></li>
                <li><Link to="/backtest" className="text-surface-400 hover:text-primary-400 transition-colors text-sm">Backtest</Link></li>
                <li><Link to="/signup" className="text-surface-400 hover:text-primary-400 transition-colors text-sm">Create Account</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-surface-100 uppercase tracking-wider mb-4">Legal</h4>
              <ul className="space-y-3">
                <li><Link to="/terms" className="text-surface-400 hover:text-primary-400 transition-colors text-sm">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-surface-400 hover:text-primary-400 transition-colors text-sm">Privacy Policy</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm font-semibold text-surface-100 uppercase tracking-wider mb-4">Contact</h4>
              <ul className="space-y-3">
                <li>
                  <a href="mailto:support@colvix.net" className="text-surface-400 hover:text-primary-400 transition-colors text-sm">
                    support@colvix.net
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="border-t border-surface-800/50 pt-8 mb-8">
            <div className="space-y-3 text-xs text-surface-100 leading-relaxed">
              <p>
                <strong className="text-white">Important Disclaimer:</strong> Colvix LLC provides TradeCraft as a platform for algorithmic strategy development and rule-based trading research. 
                <strong className="text-white"> We are not a broker, investment advisor, or fiduciary.</strong> Our platform is designed to help you make informed, independent investment decisions.
              </p>
              <p>
                All data, analyses, backtests, metrics, signals, and platform features are provided "AS-IS" without any warranties or guarantees. 
                <strong className="text-white"> Past performance is not indicative of future results.</strong> Investing involves risk, including the possible loss of principal. 
                We do not guarantee performance and bear no responsibility for your trading activities or investment decisions.
              </p>
              <p>
                We strongly advise exercising sound judgment and conducting comprehensive research and due diligence prior to trading or investing. 
                This is not an offer, solicitation of an offer, or advice to buy or sell securities.
              </p>
              <p>
                Trademarks and logos are the property of their respective owners and do not represent endorsements of any kind. 
                Unless otherwise noted, Colvix LLC and its affiliates are not partners, affiliates, or licensees of these companies.
              </p>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-surface-100">
            <span>© {new Date().getFullYear()} TradeCraft by Colvix LLC. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="hover:text-surface-300 transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-surface-300 transition-colors">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
      <HelpChat />
    </div>
  );
}
